import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, Edit, Trash2, Save, RefreshCw, BookOpen, HelpCircle, Rocket, 
  FileText, Eye, EyeOff, Headphones, Search, GripVertical, Palette
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const ICONS = [
  { id: 'rocket', icon: Rocket, label: 'Fusée' },
  { id: 'help-circle', icon: HelpCircle, label: 'Aide' },
  { id: 'book-open', icon: BookOpen, label: 'Livre' },
  { id: 'headphones', icon: Headphones, label: 'Support' },
  { id: 'file-text', icon: FileText, label: 'Document' },
  { id: 'search', icon: Search, label: 'Recherche' },
];

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'
];

const CMSKnowledgeBasePage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('articles');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [articleModal, setArticleModal] = useState({ open: false, data: null });
  const [categoryModal, setCategoryModal] = useState({ open: false, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, type: null, id: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [articlesRes, categoriesRes] = await Promise.all([
        api.get('/cms/knowledge/articles'),
        api.get('/cms/knowledge/categories')
      ]);
      setArticles(articlesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveArticle = async () => {
    try {
      const data = articleModal.data;
      if (data.id) {
        await api.put(`/cms/knowledge/articles/${data.id}`, data);
        toast.success('Article mis à jour');
      } else {
        await api.post('/cms/knowledge/articles', data);
        toast.success('Article créé');
      }
      setArticleModal({ open: false, data: null });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleSaveCategory = async () => {
    try {
      const data = categoryModal.data;
      if (data.id && !data.id.startsWith('kb-cat-')) {
        await api.put(`/cms/knowledge/categories/${data.id}`, data);
        toast.success('Catégorie mise à jour');
      } else {
        await api.post('/cms/knowledge/categories', data);
        toast.success('Catégorie créée');
      }
      setCategoryModal({ open: false, data: null });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteModal.type === 'article') {
        await api.delete(`/cms/knowledge/articles/${deleteModal.id}`);
        toast.success('Article supprimé');
      } else {
        await api.delete(`/cms/knowledge/categories/${deleteModal.id}`);
        toast.success('Catégorie supprimée');
      }
      setDeleteModal({ open: false, type: null, id: null });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getCategoryInfo = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat || { name: 'Non classé', color: '#64748b' };
  };

  const filteredArticles = articles.filter(article =>
    article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Base de connaissances</h1>
          <p className="text-slate-500">Gérez les articles d'aide de votre site</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCategoryModal({ open: true, data: { name: '', description: '', icon: 'file-text', color: '#3b82f6', order: 99 } })}>
            <Plus className="w-4 h-4 mr-2" />
            Catégorie
          </Button>
          <Button onClick={() => setArticleModal({ open: true, data: { title: '', excerpt: '', content: '', category: categories[0]?.id || '', status: 'draft', tags: [], order: 99 } })}>
            <Plus className="w-4 h-4 mr-2" />
            Article
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="articles">
            <FileText className="w-4 h-4 mr-2" />
            Articles ({articles.length})
          </TabsTrigger>
          <TabsTrigger value="categories">
            <BookOpen className="w-4 h-4 mr-2" />
            Catégories ({categories.length})
          </TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles" className="mt-4">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Rechercher un article..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {filteredArticles.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">Aucun article</h3>
                <p className="text-slate-500 mb-4">Créez votre premier article d'aide</p>
                <Button onClick={() => setArticleModal({ open: true, data: { title: '', excerpt: '', content: '', category: categories[0]?.id || '', status: 'draft', tags: [], order: 99 } })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un article
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredArticles.map(article => {
                const catInfo = getCategoryInfo(article.category);
                return (
                  <Card key={article.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-slate-900 truncate">{article.title}</h3>
                            <Badge 
                              variant="secondary" 
                              style={{ backgroundColor: catInfo.color + '20', color: catInfo.color }}
                            >
                              {catInfo.name}
                            </Badge>
                            {article.status === 'published' ? (
                              <Badge className="bg-green-100 text-green-700">
                                <Eye className="w-3 h-3 mr-1" /> Publié
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-500">
                                <EyeOff className="w-3 h-3 mr-1" /> Brouillon
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 line-clamp-2">{article.excerpt}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span>{article.views || 0} vues</span>
                            <span>👍 {article.helpful_yes || 0}</span>
                            <span>👎 {article.helpful_no || 0}</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setArticleModal({ open: true, data: article })}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteModal({ open: true, type: 'article', id: article.id })}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {categories.map(cat => {
              const IconComponent = ICONS.find(i => i.id === cat.icon)?.icon || FileText;
              const articleCount = articles.filter(a => a.category === cat.id).length;
              
              return (
                <Card key={cat.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: cat.color + '20' }}
                      >
                        <IconComponent className="w-5 h-5" style={{ color: cat.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900">{cat.name}</h3>
                        <p className="text-sm text-slate-500">{cat.description}</p>
                        <p className="text-xs text-slate-400 mt-1">{articleCount} article{articleCount > 1 ? 's' : ''}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setCategoryModal({ open: true, data: cat })}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteModal({ open: true, type: 'category', id: cat.id })}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Article Modal */}
      <Dialog open={articleModal.open} onOpenChange={(open) => !open && setArticleModal({ open: false, data: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {articleModal.data?.id ? 'Modifier l\'article' : 'Nouvel article'}
            </DialogTitle>
          </DialogHeader>
          
          {articleModal.data && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input 
                  value={articleModal.data.title || ''} 
                  onChange={(e) => setArticleModal({...articleModal, data: {...articleModal.data, title: e.target.value}})}
                  placeholder="Comment faire X ?"
                />
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select 
                    value={articleModal.data.category || ''} 
                    onValueChange={(v) => setArticleModal({...articleModal, data: {...articleModal.data, category: v}})}
                  >
                    <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }}></span>
                            {cat.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select 
                    value={articleModal.data.status || 'draft'} 
                    onValueChange={(v) => setArticleModal({...articleModal, data: {...articleModal.data, status: v}})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Brouillon</SelectItem>
                      <SelectItem value="published">Publié</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Extrait</Label>
                <Textarea 
                  value={articleModal.data.excerpt || ''} 
                  onChange={(e) => setArticleModal({...articleModal, data: {...articleModal.data, excerpt: e.target.value}})}
                  placeholder="Brève description de l'article"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Contenu *</Label>
                <Textarea 
                  value={articleModal.data.content || ''} 
                  onChange={(e) => setArticleModal({...articleModal, data: {...articleModal.data, content: e.target.value}})}
                  placeholder="Contenu de l'article (HTML ou Markdown supporté)"
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tags (séparés par des virgules)</Label>
                <Input 
                  value={(articleModal.data.tags || []).join(', ')} 
                  onChange={(e) => setArticleModal({...articleModal, data: {...articleModal.data, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)}})}
                  placeholder="aide, tutoriel, guide"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Ordre d'affichage</Label>
                <Input 
                  type="number"
                  value={articleModal.data.order || 99} 
                  onChange={(e) => setArticleModal({...articleModal, data: {...articleModal.data, order: parseInt(e.target.value) || 99}})}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleModal({ open: false, data: null })}>
              Annuler
            </Button>
            <Button onClick={handleSaveArticle}>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={categoryModal.open} onOpenChange={(open) => !open && setCategoryModal({ open: false, data: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {categoryModal.data?.id && !categoryModal.data.id.startsWith('kb-cat-') ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
          </DialogHeader>
          
          {categoryModal.data && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input 
                  value={categoryModal.data.name || ''} 
                  onChange={(e) => setCategoryModal({...categoryModal, data: {...categoryModal.data, name: e.target.value}})}
                  placeholder="FAQ"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  value={categoryModal.data.description || ''} 
                  onChange={(e) => setCategoryModal({...categoryModal, data: {...categoryModal.data, description: e.target.value}})}
                  placeholder="Questions fréquentes"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Icône</Label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(iconInfo => {
                    const IconComponent = iconInfo.icon;
                    return (
                      <button
                        key={iconInfo.id}
                        type="button"
                        onClick={() => setCategoryModal({...categoryModal, data: {...categoryModal.data, icon: iconInfo.id}})}
                        className={cn(
                          "w-10 h-10 rounded-lg border flex items-center justify-center transition-all",
                          categoryModal.data.icon === iconInfo.id 
                            ? "border-cyan-500 bg-cyan-50" 
                            : "border-slate-200 hover:border-slate-300"
                        )}
                        title={iconInfo.label}
                      >
                        <IconComponent className="w-5 h-5" style={{ color: categoryModal.data.color }} />
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Couleur</Label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setCategoryModal({...categoryModal, data: {...categoryModal.data, color}})}
                      className={cn(
                        "w-8 h-8 rounded-full transition-all",
                        categoryModal.data.color === color ? "ring-2 ring-offset-2 ring-slate-400" : ""
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Ordre d'affichage</Label>
                <Input 
                  type="number"
                  value={categoryModal.data.order || 99} 
                  onChange={(e) => setCategoryModal({...categoryModal, data: {...categoryModal.data, order: parseInt(e.target.value) || 99}})}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryModal({ open: false, data: null })}>
              Annuler
            </Button>
            <Button onClick={handleSaveCategory}>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, type: null, id: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Êtes-vous sûr de vouloir supprimer {deleteModal.type === 'article' ? 'cet article' : 'cette catégorie'} ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, type: null, id: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSKnowledgeBasePage;
